import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getTherapists } from "../../redux/therapistSlice";
import { getBranches } from "../../redux/branchSlice";
import { getLanguages } from "../../redux/languageSlice";

const UserPageSlider = ({ selectedBranchId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const scrollContainers = useRef([]);
  const dispatch = useDispatch();

  // Redux store'dan terapist ve branch verilerini çekme
  const therapistsState = useSelector((state) => state.therapist);
  const therapists = React.useMemo(
    () => therapistsState?.therapists || [],
    [therapistsState?.therapists]
  );
  const {
    branches,
    loading: branchesLoading,
    error: branchesError,
  } = useSelector((state) => state.branches);

  const languageState = useSelector((state) => state.languages);
  const fetchedLanguages = languageState?.fetchedLanguages || [];

  // Branch bazlı terapistleri gruplama için state
  const [therapistsByBranch, setTherapistsByBranch] = useState({});

  // Terapist ve branch verilerini çek
  useEffect(() => {
    dispatch(getBranches());
    dispatch(getTherapists());
    dispatch(getLanguages());
  }, [dispatch]);

  // Terapistleri branch'lara göre gruplandır
  useEffect(() => {
    if (
      therapists &&
      therapists.length > 0 &&
      branches &&
      branches.length > 0
    ) {
      const grouped = {};

      therapists.forEach((therapist) => {
        if (therapist.isActive === false) return;
        const branchIds = therapist.branchIds || [];

        branchIds.forEach((branchId) => {
          const branch = branches.find((b) => b._id === branchId);
          if (!branch) return;

          if (!grouped[branchId]) {
            grouped[branchId] = {
              branchName: branch.name,
              therapists: [],
            };
          }

          grouped[branchId].therapists.push(therapist);
        });
      });

      setTherapistsByBranch(grouped);
    }
  }, [therapists, branches]);


  const handleScroll = (index, value) => {
    const scrollContainer = scrollContainers.current[index];
    if (scrollContainer) {
      const maxScroll =
        scrollContainer.scrollWidth - scrollContainer.clientWidth;
      scrollContainer.scrollLeft = (value / 100) * maxScroll;
    }
  };

  // Terapiste tıklandığında sadece yönlendir, ID'yi Redux'a kaydetme
  const handleTherapistClick = (therapistId) => {
    // Redux store'a kaydetme işlemini kaldır
    navigate(`/therapistInfo/${therapistId}`);
  };

  // Yükleme durumları
  if (branchesLoading || therapistsState.loading) {
    return <div className="text-center py-10">Yükleniyor...</div>;
  }

  // Hata durumları
  if (branchesError || therapistsState.error) {
    return (
      <div className="text-center py-10 text-red-500">
        {branchesError || therapistsState.error}
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center items-center px-4 sm:px-6 lg:px-8">
      {/* Branch'lara göre terapistleri göster */}
      {branches && branches.length > 0 ? (
        branches.map((branch, branchIndex) => {
          const branchTherapists =
            therapistsByBranch[branch._id]?.therapists || [];
          const hasBranchTherapists = branchTherapists.length > 0;

          return (
            <div
              key={branch._id}
              id={`branch-section-${branch._id}`} // ID eklendi - scroll için
              className={`mb-10 w-full max-w-[1054px] sm:h-[514px] h-auto bg-background rounded-[30px] mt-5 mx-auto overflow-hidden
                ${
                  selectedBranchId === branch._id
                    ? "ring-4 ring-primary ring-opacity-50"
                    : ""
                }`}
            >
              <h2 className="text-[18px] font-bold p-4 text-primary">
                {branch.name}
              </h2>

              {hasBranchTherapists ? (
                <>
                  <div className="relative">
                    <div
                      ref={(el) => (scrollContainers.current[branchIndex] = el)}
                      className="flex space-x-4 ml-4 pr-8 overflow-x-auto scrollbar-hide w-full"
                      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                    >
                      {branchTherapists.map((therapist, therapistIndex) => {
                        const isActive = therapist.isVerified;
                        const buttonLabel = t("scheduleNow");
                        const borderColor = isActive ? "border-cards-border" : "border-primary";
                        const buttonColor = isActive ? "bg-button-bg border-[#f4efea]" : "bg-primary";
                        // Varsayılan mock veriler için
                        const cardBackground = therapist.cardBackground || "bg-white";
                        const therapistImage = therapist.picture || "";
                        const title = therapist.title || "Therapist";
                        return (
                          <div
                            key={therapist._id}
                            className={`border-[3px] rounded-[30px] p-3 ${cardBackground} ${borderColor} w-[80vw] max-w-[300px] h-[350px] flex-shrink-0 flex flex-col`}
                          >
                            {/* Üst kısım - Profil resmi ve isim */}
                            <div className="flex items-center">
                              <img
                                src={therapistImage}
                                alt={`${therapist.name} ${therapist.surname}`}
                                className={`w-[80px] h-[80px] object-cover rounded-full border-[3px] ${borderColor}`}
                              />
                              <div className="ml-2 flex flex-col items-center w-full">
                                <h3 className="text-[18px] font-poppins italic text-primary text-center w-full truncate">
                                  {therapist.name} {therapist.surname}
                                </h3>
                                <p className="text-[14px] text-[#000000B2] font-poppins italic text-center">
                                  {t(title)}
                                </p>
                                <hr className="my-1 border-[1.5px] border-primary w-full" />
                                <div className="flex items-center justify-center gap-1">
                                  {therapist.reviews && therapist.reviews.length > 0 ? (
                                    // Yorumlar varsa dolu yıldızlar göster
                                    <>
                                      {Array(5)
                                        .fill(0)
                                        .map((_, i) => (
                                          <svg
                                            key={i}
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="currentColor"
                                            viewBox="0 0 24 24"
                                            className="w-3 h-3 text-primary"
                                          >
                                            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                                          </svg>
                                        ))}
                                    </>
                                  ) : (
                                    // Yorumlar yoksa boş yıldızlar göster
                                    <>
                                      {Array(5)
                                        .fill(0)
                                        .map((_, i) => (
                                          <svg
                                            key={i}
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                            className="w-3 h-3 text-primary"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={1.5}
                                              d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                                            />
                                          </svg>
                                        ))}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Açıklama alanı - Sabit yükseklik */}
                            <div className="mt-4 w-full h-[70px] overflow-hidden">
                              {therapist.bio ? (
                                <div
                                  className="font-poppins italic text-[16px] leading-[20px] text-[#000000] line-clamp-3"
                                  dangerouslySetInnerHTML={{ __html: therapist.bio }}
                                />
                              ) : (
                                <p className="font-poppins italic text-[14px] leading-[20px] text-gray-400">
                                  This therapist has not added a description yet.
                                </p>
                              )}
                            </div>
                            {/* Alt kısım - Tarih ve buton */}
                            <div className="flex justify-between mt-auto">
                              <div className="flex flex-col">
                                <span className="text-[10px] font-poppins font-bold text-primary px-1 uppercase mb-0.5">
                                  {t("languages")}
                                </span>
                                <hr className="border-cards-border border-[2px] rounded mb-1" />
                                {therapist.languages && therapist.languages.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {[...therapist.languages]
                                      .sort((a, b) => {
                                        const getCodeLabel = (lang) => {
                                          const langId = typeof lang === 'object' ? lang._id : lang;
                                          const found = fetchedLanguages.find(l => l._id === langId);
                                          const code = (found?.code || (typeof lang === 'object' ? lang.code : '') || '').toUpperCase();
                                          const name = (found?.name || (typeof lang === 'object' ? lang.name : '') || '').toUpperCase();
                                          return { code, name };
                                        };
                                        const labelA = getCodeLabel(a);
                                        const labelB = getCodeLabel(b);
                                        const isEnA = labelA.code === 'EN' || labelA.name === 'ENGLISH';
                                        const isEnB = labelB.code === 'EN' || labelB.name === 'ENGLISH';
                                        if (isEnA && !isEnB) return -1;
                                        if (!isEnA && isEnB) return 1;
                                        return 0;
                                      })
                                      .map((langData, idx) => {
                                        const langId = typeof langData === 'object' ? langData._id : langData;
                                        const foundLang = fetchedLanguages.find(l => l._id === langId);
                                        const displayLabel = foundLang 
                                          ? (foundLang.code || foundLang.name) 
                                          : (typeof langData === 'object' ? (langData.code || langData.name) : '...');
                                        
                                        return (
                                          <span 
                                            key={idx} 
                                            className="text-[9px] font-poppins font-bold text-primary bg-tertiary px-1 rounded uppercase"
                                          >
                                            {displayLabel}
                                          </span>
                                        );
                                      })}
                                  </div>
                                )}
                              </div>
                              <button
                                className={`py-1 px-3 w-full max-w-[180px] h-[36px] rounded-full border-[2px] font-roboto text-[16px] text-white transition-transform transform hover:scale-105 active:scale-95 duration-300 ease-in-out ${buttonColor}`}
                                onClick={() => handleTherapistClick(therapist._id)}
                              >
                                {buttonLabel}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="w-full flex justify-center mt-2 mb-3">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      className="w-2/3 h-1.5 bg-[#024202] rounded-full appearance-none cursor-pointer border-[1.5px] slider-thumb"
                      onChange={(e) => handleScroll(branchIndex, e.target.value)}
                    />
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-[400px]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-20 h-20 text-primary opacity-50 mb-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                    />
                  </svg>
                  <p className="text-[24px] font-poppins text-primary text-center">
                    Bu branşta henüz terapist bulunmuyor
                  </p>
                  <p className="text-[16px] font-poppins text-gray-600 mt-2 text-center max-w-lg">
                    En kısa sürede bu branşa terapistler eklenecektir. Lütfen
                    daha sonra kontrol edebilirsiniz.
                  </p>
                </div>
              )}
            </div>
          );
        })
      ) : (
        <div className="text-center py-10">Hiç şube bulunamadı.</div>
      )}
      <style>
        {`
          input[type="range"] {
            position: relative;
            z-index: 10;
          }

          input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 90px;
            height: 14px;
            border-radius: 50px;
            background: #625480;
            cursor: pointer;
            position: relative;
            z-index: 10;
          }
        `}
      </style>
    </div>
  );
};

export default UserPageSlider;
